import { useState } from "react";
import { useDispatch } from "react-redux";

import { signIn } from "next-auth/react";

import PropTypes from "prop-types";

import { useResendOtp, useVerifyOtp } from "@/api/client/auth";
import { useGetNotificationsData } from "@/api/client/notifications";
import PrimaryBtn from "@/components/actions/button/primaryBtn";
import AppOtpInput from "@/components/data-input/input-otp";
import OtpError from "@/components/feedback/OtpError";
import { errorToast, successToast } from "@/components/feedback/toast";
import useCountdown from "@/hooks/useCountdown";
import { removeGuestUser } from "@/services/guest-user";
import { updateCartItemCount } from "@/store/feature/cartSlice";
import { updateWishlistItemCount } from "@/store/feature/wishlistSlice";
import { authorizedRedirection } from "@/utils/helpers";

function OtpConfirm(props) {
  const {
    loginData,
    redirectToRegister,
    onChangePhoneNumber,
    updateLoginData,
  } = props;
  const OTP_LENGTH = 6;
  const SECOND_AFTER_RESEND_OTP = 120;

  const [otp, setOtp] = useState("");
  const [optError, setOtpError] = useState("");

  const dispatch = useDispatch();

  const { minutes, seconds, timerOver, resetTimer } = useCountdown(
    SECOND_AFTER_RESEND_OTP
  );

  const { refetch: refetchNotificaitonData } = useGetNotificationsData({
    enabled: false,
    onSuccess: (data) => {
      dispatch(updateCartItemCount(data.cartProductCount));
      dispatch(updateWishlistItemCount(data.wishlistProductCount));
    },
  });

  const { isLoading: isOtpLoading, mutate: verifyOtp } = useVerifyOtp({
    onSuccess: async (otpVerifyData) => {
      if (otpVerifyData.data.new_customer) {
        redirectToRegister();
        updateLoginData((prevState) => ({
          ...prevState,
          signature: otpVerifyData.data.signature,
        }));
        return;
      }

      const user = {
        token: otpVerifyData?.data?.token,
        name: otpVerifyData?.data?.customer?.full_name,
        email: otpVerifyData?.data?.customer?.email_address,
        phone: otpVerifyData?.data?.customer?.phone_number,
      };

      const res = await signIn("credentials", { redirect: false, ...user });
      if (res) {
        authorizedRedirection();
        removeGuestUser();
        refetchNotificaitonData();
      }
    },
    onError: (otpVerifyError) => {
      setOtpError(
        otpVerifyError.message || "Wrong OTP number. Please try again"
      );
    },
  });

  const { mutate: resendOtp, isLoading: isOtpResending } = useResendOtp({
    onSuccess: ({ data }) => {
      setOtp("");
      resetTimer();
      updateLoginData(data.data);
      successToast({ message: "Otp Resend success" });
    },
    onError: () => {
      errorToast({ message: "Failed to resend otp. try some time later" });
    },
  });

  const handleOtpSubmit = (e) => {
    e.preventDefault();

    if (!otp || otp?.toString().length !== OTP_LENGTH) {
      setOtpError("Invalid Otp");
      return;
    }
    verifyOtp({
      otp: otp,
      phone: loginData?.phone,
      signature: loginData?.signature,
    });
  };

  const handleResendOtp = () => {
    resendOtp({ phone: loginData.phone });
  };

  return (
    <form className="w-full" onSubmit={handleOtpSubmit}>
      <h1 className="mb-[1rem] text-[1.25rem] font-semibold tracking-[0.14px] text-grayDisplay-700 md:text-[1.75rem]">
        One Time Password
      </h1>
      <p className="text-grayDisplay-400">
        Enter the 6-digit code sent to your number
      </p>
      <p className="mb-[1rem] mt-[0.625rem] font-semibold text-grayDisplay-900 xl:mb-[1.75rem] xl:mt-[1rem]">
        &quot;{loginData?.phone}&quot;
      </p>
      <AppOtpInput otpValue={otp} onOtpChange={(optData) => setOtp(optData)} />

      {!timerOver && (
        <p className="mt-[1rem] text-grayDisplay-700 xl:mt-[1.875rem]">
          Resend Code in {minutes}:{seconds}
        </p>
      )}
      {timerOver && (
        <button
          type="button"
          onClick={handleResendOtp}
          disabled={isOtpResending}
          className="mt-[1rem] text-primary disabled:cursor-wait xl:mt-[1.875rem]"
        >
          Resend OTP!
        </button>
      )}

      <p className="mt-[1rem] font-medium text-grayDisplay-900 underline xl:mt-[1.625rem]">
        <span className="cursor-pointer" onClick={onChangePhoneNumber}>
          Change phone number
        </span>
      </p>

      <PrimaryBtn
        btnText="Confirm"
        isLoading={isOtpLoading}
        className="mt-[1rem] h-[3.5rem] w-full sm:max-w-[29rem] xl:mt-[1.875rem]"
      />

      {optError && <OtpError errorMessage={optError} />}
    </form>
  );
}

OtpConfirm.propTypes = {
  loginData: PropTypes.shape({
    phone: PropTypes.string,
    signature: PropTypes.string,
  }),
  redirectToRegister: PropTypes.func,
  onChangePhoneNumber: PropTypes.func,
  updateLoginData: PropTypes.func,
};

export default OtpConfirm;
